import { useState, useEffect, useCallback, useMemo } from "react";
import { useTenant } from "../../../hooks/useTenant";
import EventService from "../../../services/event_service";
import { useClassMembers } from "./useClassMembers";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../services/firebase";
import { COLLECTIONS } from "../../../services/collections";
import { toast } from "react-toastify";
import GamificationService from "../../../services/gamification_service";
import GamificationEngineService from "../../../services/gamification_engine";

/**
 * useExtraEvents
 * Hook de lógica para a aba "Extras" da página de Avaliação.
 * Busca eventos extras ativos e gerencia a premiação por membros da turma selecionada.
 */
export const useExtraEvents = ({ classId } = {}) => {
    const { idTenant, user } = useTenant();

    const [extraEvents, setExtraEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [awardedMemberIds, setAwardedMemberIds] = useState([]);
    const [checkedMemberIds, setCheckedMemberIds] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [loadingAwarded, setLoadingAwarded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Usar o hook de membros da turma (mesmo padrão das outras abas)
    const withLoadingNoop = useCallback(async (_key, fn) => fn(), []);
    const { members: classMembers } = useClassMembers({ classId, withLoading: withLoadingNoop });

    // Carregar eventos extras ativos
    useEffect(() => {
        if (!idTenant) return;

        const loadEvents = async () => {
            setLoading(true);
            try {
                const events = await EventService.getAll(idTenant);
                const now = new Date();
                const activeExtras = (events || []).filter(evt => {
                    if (evt.type !== "extra") return false;
                    const start = evt.startDate ? new Date(evt.startDate) : null;
                    const end = evt.endDate ? new Date(evt.endDate) : null;
                    if (start && now < start) return false;
                    if (end) {
                        const endOfDay = new Date(end);
                        endOfDay.setHours(23, 59, 59, 999);
                        if (now > endOfDay) return false;
                    }
                    return true;
                });
                setExtraEvents(activeExtras);
            } catch (err) {
                console.error("[useExtraEvents] Erro ao carregar eventos:", err);
            } finally {
                setLoading(false);
            }
        };

        loadEvents();
    }, [idTenant]);

    // Reset seleção quando troca a turma
    useEffect(() => {
        setSelectedEvent(null);
        setAwardedMemberIds([]);
        setCheckedMemberIds(new Set());
    }, [classId]);

    // Quando seleciona um evento, buscar quem já foi premiado (entre os membros da turma)
    useEffect(() => {
        if (!idTenant || !selectedEvent?.id || !classMembers?.length) {
            setAwardedMemberIds([]);
            setCheckedMemberIds(new Set());
            return;
        }

        const loadAwarded = async () => {
            setLoadingAwarded(true);
            try {
                const results = await Promise.allSettled(
                    classMembers.map(async (member) => {
                        const achRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, member.id, COLLECTIONS.AWARDED_ACHIEVEMENTS, selectedEvent.id);
                        const snap = await getDoc(achRef);
                        return snap.exists() ? member.id : null;
                    })
                );
                const awarded = results
                    .filter(r => r.status === "fulfilled" && r.value !== null)
                    .map(r => r.value);
                setAwardedMemberIds(awarded);
                setCheckedMemberIds(new Set(awarded));
            } catch (err) {
                console.error("[useExtraEvents] Erro ao buscar premiados:", err);
            } finally {
                setLoadingAwarded(false);
            }
        };

        loadAwarded();
    }, [idTenant, selectedEvent?.id, classMembers]);

    // Toggle checkbox de membro
    const toggleMember = useCallback((idMember) => {
        setCheckedMemberIds(prev => {
            const next = new Set(prev);
            if (next.has(idMember)) next.delete(idMember);
            else next.add(idMember);
            return next;
        });
    }, []);

    // Verificar se existem mudanças
    const isDirty = useMemo(() => {
        const currentSet = new Set(checkedMemberIds);
        const awardedSet = new Set(awardedMemberIds);
        if (currentSet.size !== awardedSet.size) return true;
        for (const id of currentSet) {
            if (!awardedSet.has(id)) return true;
        }
        return false;
    }, [checkedMemberIds, awardedMemberIds]);

    // Salvar premiações
    const handleSave = useCallback(async (xpReward = 0) => {
        if (!idTenant || !selectedEvent?.id) return;
        setIsSaving(true);

        try {
            const currentSet = new Set(checkedMemberIds);
            const awardedSet = new Set(awardedMemberIds);

            const toAward = [...currentSet].filter(id => !awardedSet.has(id));
            const toRevoke = [...awardedSet].filter(id => !currentSet.has(id));

            if (toAward.length > 0) {
                await EventService.awardExtraEvent(idTenant, selectedEvent, toAward, user, xpReward);
            }

            for (const id of toRevoke) {
                await EventService.revokeExtraEvent(idTenant, selectedEvent.id, id, user);
            }

            if (toAward.length > 0) {
                // Disparo do Motor de Gamificação em Background para avaliar subida de nível
                try {
                    const gamificationSettings = await GamificationService.getSettings(idTenant);
                    if (gamificationSettings) {
                        Promise.allSettled(
                            toAward.map(idMember =>
                                GamificationEngineService.evaluateMemberGamification(
                                    idTenant, idMember, gamificationSettings, user
                                )
                            )
                        ).catch(err => console.error('[useExtraEvents] Erro no GamificationEngine:', err));
                    }
                } catch (err) {
                    console.warn('[useExtraEvents] Não foi possível executar GamificationEngine:', err);
                }
            }

            setAwardedMemberIds([...currentSet]);
            toast.success(`Insígnias atualizadas! (+${toAward.length} / -${toRevoke.length})`);
        } catch (err) {
            console.error("[useExtraEvents] Erro ao salvar:", err);
            toast.error("Erro ao salvar insígnias.");
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, selectedEvent, checkedMemberIds, awardedMemberIds, user]);

    return {
        extraEvents,
        classMembers: classMembers || [],
        selectedEvent,
        setSelectedEvent,
        checkedMemberIds,
        awardedMemberIds,
        toggleMember,
        isDirty,
        loading,
        loadingAwarded,
        isSaving,
        handleSave
    };
};
